import { getServices } from "../../../api/getServices";
import { EditorSettings } from "../../types/EditorSettings";
import { ViewType } from "../../types/ViewType";
import { getDeviceSettings } from "../selectors/session";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { createEditorSettings } from "../utils/createEditorSettings";

export enum ActionKey {
  SET_DEVICE_SETTINGS = "SET_DEVICE_SETTINGS",
}

interface SetEditorSettingsAction
  extends AppAction<
    ActionKey.SET_DEVICE_SETTINGS,
    {
      settings: EditorSettings;
    }
  > {}

export type EditorSettingsAction = SetEditorSettingsAction;

const setEditorSettings = (settings: EditorSettings): SetEditorSettingsAction =>
  getStore().dispatch({
    type: ActionKey.SET_DEVICE_SETTINGS,
    payload: { settings },
  });

/** Persist editor settings to Redux and write to disk. */
export const writeEditorSettings = (settings: EditorSettings) => {
  setEditorSettings(settings);
  getServices().kernelStorageClient.writeEditorSettings(settings);
};

/** Read editor settings from disk and hydrate Redux. */
export const readEditorSettings = () => {
  const settings = createEditorSettings(
    getServices().kernelStorageClient.readEditorSettings()
  );
  if (settings !== null) {
    setEditorSettings(settings);
  }
};

/** Changes view visibility and persists setting to Redux / disk. */
export const setEditorViewVisibility = (
  viewType: ViewType,
  isVisible: boolean
) => {
  const settings = getDeviceSettings(getStore().getState());
  // Get a filtered copy of view.
  const enabledViews = settings.enabledViews.filter(view => view !== viewType);

  // Since this is a copy of the original array, its ok to mutate.
  if (isVisible) {
    enabledViews.push(viewType);
  }

  // Sort the view names to keep them consistent.
  enabledViews.sort();

  writeEditorSettings({
    ...settings,
    enabledViews,
  });
};

/** Changes view visibility and persists setting to Redux / disk. */
export const setEditorViewExpansion = (
  viewType: ViewType,
  isExpanded: boolean
) => {
  const state = getStore().getState();
  const settings = getDeviceSettings(state);
  // Get a filtered copy of view.
  const expandedViews = settings.expandedViews.filter(
    view => view !== viewType
  );

  // Since this is a copy of the original array, its ok to mutate.
  if (isExpanded) {
    expandedViews.push(viewType);
  }

  // Sort the view names to keep them consistent.
  expandedViews.sort();
  writeEditorSettings({
    ...settings,
    expandedViews,
  });
};
